import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import DeductionForm from '../../components/Form/DeductionForm';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { Deduction, TableColumn } from '../../types';
import { useDeductions, useCreateDeduction, useUpdateDeduction, useDeleteDeduction } from '../../hooks/useDeductions';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { useInvoiceHires } from '../../hooks/useInvoiceHires';
import { useToast } from '../../contexts/ToastContext';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';

const Deductions: React.FC = () => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState<Deduction | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');
  const [reasonFilter, setReasonFilter] = useState<string>('all');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 }
  }), []);

  const { data: deductions = [], isLoading, error, refetch } = useDeductions(queryOptions);
  const { data: clients = [] } = useClients(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);
  const { data: invoiceHires = [] } = useInvoiceHires(queryOptions);

  const createMutation = useCreateDeduction(() => {
    showToast('Deduction created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDeduction(undefined);
  });

  const updateMutation = useUpdateDeduction(() => {
    showToast('Deduction updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingDeduction(undefined);
  });

  const deleteMutation = useDeleteDeduction(() => {
    showToast('Deduction deleted successfully', 'success');
    refetch();
  });

  const getClientName = useCallback((clientId: string) => {
    const client = clients.find(c => (c as any).documentId === clientId || String(c.id) === String(clientId));
    return client ? client.name : '-';
  }, [clients]);

  const getVesselName = (vesselId?: string) => {
    if (!vesselId) return '-';
    const vessel = branches.find(b => (b as any).documentId === vesselId || String(b.id) === String(vesselId));
    return vessel ? vessel.name : '-';
  };

  // The form stores the invoice-hire documentId; show the invoice number instead
  const getInvoiceLabel = useCallback((invoiceId: string) => {
    if (!invoiceId) return '-';
    const inv = invoiceHires.find(i => i.documentId === invoiceId || String(i.id) === String(invoiceId));
    return inv ? inv.invoiceNumber : invoiceId;
  }, [invoiceHires]);

  const formatReason = (reason: string) => reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const formatType = (type: string) => type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const uniqueReasons = useMemo(() => {
    const reasons = Array.from(new Set(deductions.map(d => d.reason)));
    return reasons.filter(Boolean);
  }, [deductions]);

  const filteredDeductions = useMemo(() => {
    return deductions.filter(deduction => {
      const matchesSearch = !searchTerm || (
        deduction.deductionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getInvoiceLabel(deduction.invoiceId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getClientName(deduction.clientId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatReason(deduction.reason).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || deduction.status === statusFilter;
      const matchesType = typeFilter === 'all' || deduction.type === typeFilter;
      const matchesClient = clientFilter === 'all' || getClientName(deduction.clientId) === getClientName(clientFilter);
      const matchesReason = reasonFilter === 'all' || deduction.reason === reasonFilter;
      const deductionDate = new Date(deduction.deductionDate);
      const matchesDateFrom = !dateFromFilter || deductionDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || deductionDate <= new Date(dateToFilter);
      const amount = deduction.type === 'fixed' ? deduction.amount : deduction.percentage;
      const matchesAmountFrom = !amountFromFilter || amount >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || amount <= parseFloat(amountToFilter);
      return matchesSearch && matchesStatus && matchesType && matchesClient && matchesReason &&
        matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [deductions, searchTerm, statusFilter, typeFilter, clientFilter, reasonFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, getClientName, getInvoiceLabel]);

  const buildExportRow = (deduction: Deduction) => ({
    'Deduction Number': deduction.deductionNumber,
    'Invoice': getInvoiceLabel(deduction.invoiceId),
    'Client': getClientName(deduction.clientId),
    'Vessel/Headquarters': getVesselName(deduction.vesselId),
    'Reason': formatReason(deduction.reason),
    'Type': formatType(deduction.type),
    'Amount': deduction.type === 'fixed' ? deduction.amount : `${deduction.percentage}%`,
    'Status': deduction.status,
    'Deduction Date': new Date(deduction.deductionDate).toLocaleDateString(),
    'Notes': deduction.notes || ''
  });

  const handleExport = () => {
    exportToExcel(filteredDeductions.map(buildExportRow), `deductions_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    printAsExcel(filteredDeductions.map(buildExportRow), 'Deductions');
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'applied', label: 'Applied' }, { value: 'cancelled', label: 'Cancelled' }
    ]},
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter, options: [
      { value: 'all', label: 'All Types' }, { value: 'percentage', label: 'Percentage' }, { value: 'fixed', label: 'Fixed Amount' }, { value: 'performance_bond', label: 'Performance Bond' }, { value: 'tax', label: 'Tax' }
    ]},
    { key: 'client', label: 'Client', type: 'select', value: clientFilter, onChange: setClientFilter, options: [
      { value: 'all', label: 'All Clients' }, ...clients.map(c => ({ value: (c as any).documentId || String(c.id), label: c.name }))
    ]},
    { key: 'reason', label: 'Reason', type: 'select', value: reasonFilter, onChange: setReasonFilter, options: [
      { value: 'all', label: 'All Reasons' }, ...uniqueReasons.map(r => ({ value: r, label: formatReason(r) }))
    ]},
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999', step: '0.01' },
  ];

  const columns: TableColumn[] = [
    { key: 'deductionNumber', label: 'Deduction #', sortable: true },
    { key: 'invoiceId', label: 'Invoice', sortable: true, render: (value: string) => getInvoiceLabel(value) },
    { key: 'clientId', label: 'Client', sortable: true, render: (value: string) => getClientName(value) },
    { key: 'vesselId', label: 'Vessel', sortable: true, render: (value?: string) => getVesselName(value) },
    { key: 'type', label: 'Type', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        value === 'percentage' ? 'bg-blue-100 text-blue-800' : value === 'fixed' ? 'bg-green-100 text-green-800' :
        value === 'performance_bond' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
      }`}>{formatType(value)}</span>
    )},
    { key: 'reason', label: 'Reason', sortable: true, render: (value: string) => formatReason(value) },
    { key: 'amount', label: 'Amount', sortable: true, render: (value: number, row: Deduction) => {
      if (row.type === 'percentage') return `${row.percentage}%`;
      const currencyCode = resolveCurrencyCode(row.currency);
      const symbol = currencyCode === 'USD' ? '$' : 'EGP';
      return `${symbol}${value.toLocaleString()}`;
    }},
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'active' ? 'bg-green-100 text-green-800' : value === 'applied' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
      }`}>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
    )},
    { key: 'deductionDate', label: 'Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const handleEdit = (deduction: Deduction) => {
    setEditingDeduction(deduction);
    setShowForm(true);
  };

  const handleDelete = async (deduction: Deduction) => {
    if (window.confirm(`Are you sure you want to delete deduction ${deduction.deductionNumber}?`)) {
      try {
        await deleteMutation.execute(deduction);
      } catch (error) {
        console.error('Delete deduction error:', error);
      }
    }
  };

  const handleAdd = () => {
    setEditingDeduction(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Deduction>) => {
    if (editingDeduction) {
      try {
        const updatedData = { ...editingDeduction, ...data };
        await updateMutation.execute({ id: editingDeduction.id, data: updatedData });
      } catch (error) {
        console.error('Update deduction error:', error);
      }
    } else {
      try {
        await createMutation.execute(data);
      } catch (error) {
        console.error('Create deduction error:', error);
      }
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDeduction(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setClientFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setAmountFromFilter('');
    setAmountToFilter('');
    setReasonFilter('all');
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading deductions" onRetry={() => refetch()} entityName="deductions" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <DeductionForm deduction={editingDeduction} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Invoice Deductions" description="Manage invoice deductions and adjustments" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(['active', 'applied', 'cancelled'] as const).map(status => {
          const count = deductions.filter(d => d.status === status).length;
          const totalAmount = deductions.filter(d => d.status === status).reduce((sum, d) => sum + (d.type === 'fixed' ? d.amount : 0), 0);
          const colors = { active: 'bg-green-100 text-green-800', applied: 'bg-blue-100 text-blue-800', cancelled: 'bg-red-100 text-red-800' };
          return (
            <div key={status} className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 capitalize">{status}</p>
                  <p className="text-2xl font-semibold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500">${totalAmount.toFixed(2)}</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              </div>
            </div>
          );
        })}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-semibold text-gray-900">{deductions.length}</p>
              <p className="text-sm text-gray-500">${deductions.reduce((sum, d) => sum + (d.type === 'fixed' ? d.amount : 0), 0).toFixed(2)}</p>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">All</span>
          </div>
        </div>
      </div>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search deductions..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={deductions.length}
        filteredCount={filteredDeductions.length}
        entityName="deductions"
        onAdd={handleAdd}
        addLabel="Add Deduction"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <Table columns={columns} data={filteredDeductions} onEdit={handleEdit} onDelete={handleDelete} onAdd={handleAdd} title="Deductions" searchPlaceholder="Search deductions..." />
      </div>
    </div>
  );
};

export default Deductions;
